import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useAppContext } from '../context/AppContext';
import axios from 'axios';
import Layout from '../components/Layout';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Spinner,
} from 'react-bootstrap';
import { Bar } from 'react-chartjs-2';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import appConstants from '../constants/appConstants';
import { formatCurrency } from '../utils/feeUtils';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import {
  FaUsers,
  FaRupeeSign,
  FaExclamationTriangle,
  FaCalendarCheck,
  FaArrowRight,
  FaSyncAlt,
  FaCheckCircle,
  FaClock,
  FaLayerGroup,
  FaFileInvoice,
  FaUserPlus,
  FaCalendarAlt,
  FaWallet,
  FaChartLine,
  FaChartBar,
  FaClipboardList,
} from 'react-icons/fa';

const getAuthToken = () => localStorage.getItem('authToken');

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const Dashboard = () => {
  const { selectedAcademicYear } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [firstLoaded, setFirstLoaded] = useState(true);
  const [cashAccounts, setCashAccounts] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [feeDueReminders, setFeeDueReminders] = useState(null);

  const [dashboardData, setDashboardData] = useState({
    total_active_students: 0,
    total_collected: 0,
    total_pending: 0,
    total_application: 0,
    recent_transactions: [],
  });

  const [feeChartData, setFeeChartData] = useState({
    labels: [],
    datasets: [
      {
        label: 'Fees Collected',
        data: [],
        backgroundColor: '#6366f1',
        borderRadius: 4,
      },
    ],
  });

  const [attendanceChartData, setAttendanceChartData] = useState({
    labels: [],
    datasets: [
      {
        label: 'Attendance on',
        data: [],
        backgroundColor: '#10b981',
        borderRadius: 4,
      },
    ],
  });

  // Fetch Cash & Bank Balances
  const fetchCashBankBalances = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;

    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}get_cash_bank_balances`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data) {
        setCashAccounts(response.data.cash_accounts || []);
        setBankAccounts(response.data.bank_accounts || []);
      }
    } catch (error) {
      console.error('Error fetching cash/bank balances:', error);
    }
  }, []);

  // Fetch Fee Due Reminders
  const fetchFeeDueReminders = useCallback(async () => {
    const token = getAuthToken();
    if (!token || !selectedAcademicYear?.id) return;

    try {
      const today = new Date().toISOString().split('T')[0];
      const endpoint =
        appConstants.BATCH_FEE_DUE?.GET_FEE_DUE_REMINDERS ||
        'get_fee_due_reminders';

      const response = await axios.post(
        `${appConstants.API_ROOT_URL}${endpoint}`,
        {
          academic_year_id: selectedAcademicYear.id,
          as_of_date: today,
          status: 'Active',
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (response.data && response.data.status === 'success') {
        setFeeDueReminders(response.data);
      } else {
        setFeeDueReminders(null);
      }
    } catch (error) {
      console.error('Error fetching fee due reminders:', error);
      setFeeDueReminders(null);
    }
  }, [selectedAcademicYear]);

  // Fetch Main Dashboard Data
  const fetchDashboardData = useCallback(async () => {
    fetchCashBankBalances();
    fetchFeeDueReminders();

    if (firstLoaded) {
      setLoading(true);
    }

    const token = getAuthToken();
    if (!token) {
      navigate('/login');
      return;
    }

    if (!selectedAcademicYear || !selectedAcademicYear.id) {
      return;
    }

    try {
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}get-dashboard-data`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { academic_year_id: selectedAcademicYear.id },
        },
      );

      if (response.data.status === 'success') {
        setFirstLoaded(false);
        const feesData = response.data.data.fees_chart;
        if (feesData && feesData.datasets) {
          feesData.datasets = feesData.datasets.map((d) => ({
            ...d,
            backgroundColor: '#6366f1',
            borderRadius: 4,
          }));
        }
        const attData = response.data.data.attendance_chart;
        if (attData && attData.datasets) {
          attData.datasets = attData.datasets.map((d) => ({
            ...d,
            backgroundColor: '#10b981',
            borderRadius: 4,
          }));
        }

        setAttendanceChartData(attData || { labels: [], datasets: [] });
        setFeeChartData(feesData || { labels: [], datasets: [] });
        setDashboardData(response.data.data);
      } else {
        if (
          response.data.message === 'Expired token' ||
          response.data.message === 'Invalid or expired token'
        ) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('userData');
          navigate('/login');
        }
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      if (firstLoaded) {
        setLoading(false);
      }
    }
  }, [
    fetchCashBankBalances,
    fetchFeeDueReminders,
    firstLoaded,
    selectedAcademicYear,
    navigate,
  ]);

  useEffect(() => {
    fetchDashboardData();

    const interval = setInterval(fetchDashboardData, 15000);
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  useEffect(() => {
    const handlePopState = () => {
      if (location.pathname === '/dashboard') {
        window.history.pushState(null, '', window.location.href);
      }
    };

    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [location]);

  // Navigate to Batch Fee Due with preselected batch
  const handleNavigateToBatch = (batchId) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      sessionStorage.setItem(
        'batch_fee_due_preserved_state',
        JSON.stringify({
          academicYearId: selectedAcademicYear?.id,
          selectedBatchId: String(batchId),
          asOfDate: today,
          studentStatus: 'Active',
          searchTerm: '',
          batchData: null,
          selectedInstallmentIds: [],
          expandedStudentIds: [],
        }),
      );
    } catch (e) {
      console.warn(e);
    }
    navigate('/batch-fee-due');
  };

  // Formatted Metrics
  const totalStudents = useMemo(() => {
    return firstLoaded && loading ? '-' : dashboardData.total_active_students;
  }, [firstLoaded, loading, dashboardData.total_active_students]);

  const totalCollectedFormatted = useMemo(() => {
    if (firstLoaded && loading) return '-';
    const val = dashboardData.total_collected;
    if (val >= 100000) {
      return `₹${(val / 100000).toFixed(2)} L`;
    }
    return `₹${(val / 1000).toFixed(1)} K`;
  }, [firstLoaded, loading, dashboardData.total_collected]);

  const totalPendingFormatted = useMemo(() => {
    if (firstLoaded && loading) return '-';
    const val = dashboardData.total_pending;
    if (val >= 100000) {
      return `₹${(val / 100000).toFixed(2)} L`;
    }
    return `₹${(val / 1000).toFixed(1)} K`;
  }, [firstLoaded, loading, dashboardData.total_pending]);

  const totalCollectedFull = useMemo(() => {
    return (dashboardData.total_collected || 0).toLocaleString('en-IN');
  }, [dashboardData.total_collected]);

  const totalPendingFull = useMemo(() => {
    return (dashboardData.total_pending || 0).toLocaleString('en-IN');
  }, [dashboardData.total_pending]);

  // Total Liquid Assets (Cash + Bank)
  const totalLiquidAssets = useMemo(() => {
    let sum = 0;
    cashAccounts.forEach((c) => (sum += parseFloat(c.balance || 0)));
    bankAccounts.forEach((b) => (sum += parseFloat(b.balance || 0)));
    return sum;
  }, [cashAccounts, bankAccounts]);

  const reminderSummary = feeDueReminders?.summary || {
    batches_count: 0,
    students_count: 0,
    installments_count: 0,
    total_amount: 0,
  };

  return (
    <Layout>
      <style>{`
        .dash-container {
          padding: 12px 18px;
        }
        .dash-kpi-card-compact {
          border: none;
          border-radius: 10px;
          color: white;
          padding: 8px 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
          transition: transform 0.15s ease;
          display: flex;
          align-items: center;
          gap: 10px;
          min-height: 68px;
          text-decoration: none;
        }
        .dash-kpi-card-compact:hover {
          transform: translateY(-2px);
          color: white;
        }
        .dash-kpi-icon-compact {
          width: 38px;
          height: 38px;
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.22);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.15rem;
          flex-shrink: 0;
        }
        .dash-pulse-badge-compact {
          display: inline-block;
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background-color: #ef4444;
          box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
          animation: pulseCompact 1.8s infinite;
        }
        @keyframes pulseCompact {
          0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); }
          70% { transform: scale(1); box-shadow: 0 0 0 6px rgba(239, 68, 68, 0); }
          100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
        }
        .dash-reminder-strip {
          background: #ffffff;
          border: 1px solid #fed7aa;
          border-left: 4px solid #f97316;
          border-radius: 8px;
          padding: 6px 12px;
          box-shadow: 0 1px 4px rgba(0,0,0,0.03);
        }
        .dash-reminder-batch-pill {
          background: #fff7ed;
          border: 1px solid #ffedd5;
          color: #9a3412;
          font-size: 0.75rem;
          font-weight: 600;
          padding: 3px 8px;
          border-radius: 6px;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .dash-reminder-batch-pill:hover {
          background: #ffedd5;
          color: #7c2d12;
          border-color: #fdba74;
        }
        .dash-card-compact {
          border: none;
          border-radius: 10px;
          box-shadow: 0 1px 6px rgba(0,0,0,0.04);
          background: #ffffff;
          height: 100%;
        }
        .dash-quick-pill {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          color: #334155;
          font-size: 0.75rem;
          font-weight: 600;
          padding: 4px 10px;
          border-radius: 6px;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 5px;
          transition: all 0.15s ease;
        }
        .dash-quick-pill:hover {
          background: #e2e8f0;
          color: #0f172a;
        }
      `}</style>

      <Container fluid className="dash-container">
        {/* Compact Header Bar */}
        <div className="d-flex align-items-center justify-content-between mb-2 pb-1">
          <div className="d-flex align-items-center gap-2">
            <h5 className="fw-bold mb-0 text-dark">Dashboard</h5>
            {selectedAcademicYear && (
              <Badge
                bg="light"
                text="dark"
                className="border px-2 py-1 small fw-semibold"
              >
                <FaCalendarAlt className="text-primary me-1" />
                {selectedAcademicYear.name}
              </Badge>
            )}
          </div>

          <div className="d-flex align-items-center gap-1">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={fetchDashboardData}
              disabled={loading}
              className="py-1 px-2 small fw-semibold"
              style={{ fontSize: '0.75rem' }}
              title="Refresh Dashboard"
            >
              <FaSyncAlt className={loading ? 'fa-spin text-primary' : ''} />
              <span className="ms-1 d-none d-sm-inline">Refresh</span>
            </Button>
          </div>
        </div>

        {/* 4 Compact KPI Cards */}
        <Row className="g-2 mb-2">
          {/* 1. Total Active Students */}
          <Col xl={3} sm={6}>
            <Link
              to="/student-list"
              className="dash-kpi-card-compact"
              style={{
                background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
              }}
            >
              <div className="dash-kpi-icon-compact">
                <FaUsers />
              </div>
              <div className="overflow-hidden">
                <div
                  style={{
                    fontSize: '0.68rem',
                    textTransform: 'uppercase',
                    opacity: 0.9,
                    fontWeight: 600,
                  }}
                >
                  Active Students
                </div>
                <div
                  style={{
                    fontSize: '1.15rem',
                    fontWeight: 'bold',
                    lineHeight: 1.1,
                  }}
                >
                  {totalStudents}
                </div>
                <div
                  style={{
                    fontSize: '0.65rem',
                    opacity: 0.85,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Applications: {dashboardData.total_application || 0}
                </div>
              </div>
            </Link>
          </Col>

          {/* 2. Fees Collected */}
          <Col xl={3} sm={6}>
            <Link
              to="/receipt"
              className="dash-kpi-card-compact"
              style={{
                background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
              }}
            >
              <div className="dash-kpi-icon-compact">
                <FaRupeeSign />
              </div>
              <div className="overflow-hidden">
                <div
                  style={{
                    fontSize: '0.68rem',
                    textTransform: 'uppercase',
                    opacity: 0.9,
                    fontWeight: 600,
                  }}
                >
                  Fees Collected
                </div>
                <div
                  style={{
                    fontSize: '1.15rem',
                    fontWeight: 'bold',
                    lineHeight: 1.1,
                  }}
                >
                  {totalCollectedFormatted}
                </div>
                <div
                  style={{
                    fontSize: '0.65rem',
                    opacity: 0.85,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Total: ₹{totalCollectedFull}
                </div>
              </div>
            </Link>
          </Col>

          {/* 3. Pending Fees */}
          <Col xl={3} sm={6}>
            <Link
              to="/fee-summary"
              className="dash-kpi-card-compact"
              style={{
                background: 'linear-gradient(135deg, #e11d48 0%, #f43f5e 100%)',
              }}
            >
              <div className="dash-kpi-icon-compact">
                <FaExclamationTriangle />
              </div>
              <div className="overflow-hidden">
                <div
                  style={{
                    fontSize: '0.68rem',
                    textTransform: 'uppercase',
                    opacity: 0.9,
                    fontWeight: 600,
                  }}
                >
                  Pending Due Balance
                </div>
                <div
                  style={{
                    fontSize: '1.15rem',
                    fontWeight: 'bold',
                    lineHeight: 1.1,
                  }}
                >
                  {totalPendingFormatted}
                </div>
                <div
                  style={{
                    fontSize: '0.65rem',
                    opacity: 0.85,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Total: ₹{totalPendingFull}
                </div>
              </div>
            </Link>
          </Col>

          {/* 4. Installments To Make Due (Reminder KPI) */}
          <Col xl={3} sm={6}>
            <Link
              to="/batch-fee-due"
              className="dash-kpi-card-compact"
              style={{
                background:
                  reminderSummary.installments_count > 0
                    ? 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)'
                    : 'linear-gradient(135deg, #475569 0%, #64748b 100%)',
              }}
            >
              <div className="dash-kpi-icon-compact">
                <FaCalendarCheck />
              </div>
              <div className="overflow-hidden">
                <div
                  style={{
                    fontSize: '0.68rem',
                    textTransform: 'uppercase',
                    opacity: 0.9,
                    fontWeight: 600,
                  }}
                >
                  To Make Due
                </div>
                <div
                  style={{
                    fontSize: '1.15rem',
                    fontWeight: 'bold',
                    lineHeight: 1.1,
                  }}
                >
                  {formatCurrency(reminderSummary.total_amount)}
                </div>
                <div
                  style={{
                    fontSize: '0.65rem',
                    opacity: 0.85,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {reminderSummary.installments_count > 0
                    ? `${reminderSummary.installments_count} in ${reminderSummary.batches_count} batches`
                    : 'All up to date'}
                </div>
              </div>
            </Link>
          </Col>
        </Row>

        {/* COMPACT FEE DUE REMINDERS STRIP */}
        {feeDueReminders && feeDueReminders.batches?.length > 0 ? (
          <div className="dash-reminder-strip mb-2 d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div className="d-flex align-items-center gap-1 text-dark fw-bold small">
                <span className="dash-pulse-badge-compact me-1" />
                <FaClock className="text-warning" />
                <span>Pending Dues:</span>
                <span className="text-danger fw-bold">
                  {formatCurrency(reminderSummary.total_amount)}
                </span>
                <span className="text-muted fw-normal small">
                  ({reminderSummary.installments_count} installments •{' '}
                  {reminderSummary.batches_count} batches)
                </span>
              </div>

              {/* Batch Pills List */}
              <div className="d-flex align-items-center gap-1 flex-wrap">
                {feeDueReminders.batches.map((b) => (
                  <button
                    key={b.batch_id}
                    type="button"
                    className="dash-reminder-batch-pill"
                    onClick={() => handleNavigateToBatch(b.batch_id)}
                    title={`Make due for ${b.class_name} ${b.division} (${b.installments_count} installments, ${formatCurrency(b.total_amount)})`}
                  >
                    <span>
                      {b.class_name}-{b.division}
                    </span>
                    <strong className="text-danger">
                      {formatCurrency(b.total_amount)}
                    </strong>
                    <FaArrowRight style={{ fontSize: '0.65rem' }} />
                  </button>
                ))}
              </div>
            </div>

            <Link
              to="/batch-fee-due"
              className="btn btn-sm btn-outline-dark py-0 px-2 fw-semibold d-flex align-items-center gap-1 ms-auto"
              style={{ fontSize: '0.75rem', height: '26px' }}
            >
              <span>Batch Fee Due</span>
              <FaArrowRight style={{ fontSize: '0.65rem' }} />
            </Link>
          </div>
        ) : feeDueReminders && feeDueReminders.batches?.length === 0 ? (
          <div className="p-1 px-2 mb-2 rounded bg-light border d-flex align-items-center justify-content-between small text-muted">
            <div className="d-flex align-items-center gap-1">
              <FaCheckCircle className="text-success small" />
              <span className="fw-semibold text-dark small">
                All fee installments are up to date
              </span>
            </div>
            <Link
              to="/batch-fee-due"
              className="text-decoration-none small text-muted"
            >
              Batch Fee Due Manager →
            </Link>
          </div>
        ) : null}

        {/* Middle Section: Charts & Liquid Accounts (Compact Height) */}
        <Row className="g-2 mb-2">
          {/* Fee Collection Trend */}
          <Col lg={5}>
            <Card className="dash-card-compact p-2">
              <div className="d-flex align-items-center justify-content-between mb-1 px-1">
                <span
                  className="fw-bold text-dark d-flex align-items-center gap-1"
                  style={{ fontSize: '0.8rem' }}
                >
                  <FaChartLine className="text-primary" />
                  Fee Collection Trend
                </span>
                <span className="text-muted" style={{ fontSize: '0.7rem' }}>
                  Recent Months
                </span>
              </div>
              <div style={{ height: '160px', position: 'relative' }}>
                <Bar
                  data={feeChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        callbacks: {
                          label: (context) =>
                            ` ₹${(context.raw || 0).toLocaleString('en-IN')}`,
                        },
                      },
                    },
                    scales: {
                      x: {
                        grid: { display: false },
                        ticks: { font: { size: 10 } },
                      },
                      y: {
                        beginAtZero: true,
                        ticks: {
                          font: { size: 10 },
                          callback: (v) =>
                            v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v,
                        },
                      },
                    },
                  }}
                />
              </div>
            </Card>
          </Col>

          {/* Class Attendance */}
          <Col lg={4}>
            <Card className="dash-card-compact p-2">
              <div className="d-flex align-items-center justify-content-between mb-1 px-1">
                <span
                  className="fw-bold text-dark d-flex align-items-center gap-1"
                  style={{ fontSize: '0.8rem' }}
                >
                  <FaChartBar className="text-success" />
                  Class Attendance
                </span>
                <Link
                  to="/attendance-management"
                  className="text-muted"
                  style={{ fontSize: '0.7rem', textDecoration: 'none' }}
                >
                  Attendance →
                </Link>
              </div>
              <div style={{ height: '160px', position: 'relative' }}>
                <Bar
                  data={attendanceChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                    },
                    scales: {
                      x: {
                        grid: { display: false },
                        ticks: { font: { size: 10 } },
                      },
                      y: {
                        beginAtZero: true,
                        ticks: { font: { size: 10 } },
                      },
                    },
                  }}
                />
              </div>
            </Card>
          </Col>

          {/* Liquid Accounts Balances */}
          <Col lg={3}>
            <Card className="dash-card-compact p-2">
              <div className="d-flex align-items-center justify-content-between mb-1 px-1">
                <span
                  className="fw-bold text-dark d-flex align-items-center gap-1"
                  style={{ fontSize: '0.8rem' }}
                >
                  <FaWallet className="text-warning" />
                  Liquid Balances
                </span>
                <Link
                  to="/account-management"
                  className="text-muted"
                  style={{ fontSize: '0.7rem', textDecoration: 'none' }}
                >
                  Accounts →
                </Link>
              </div>

              {/* Total Summary */}
              <div
                className="p-1 px-2 rounded bg-light border mb-1 d-flex justify-content-between align-items-center"
                style={{ fontSize: '0.75rem' }}
              >
                <span className="text-muted">Total Liquid:</span>
                <span className="fw-bold text-dark">
                  {formatCurrency(totalLiquidAssets)}
                </span>
              </div>

              {/* Scrollable List */}
              <div
                style={{
                  maxHeight: '120px',
                  overflowY: 'auto',
                  paddingRight: '2px',
                }}
              >
                {cashAccounts.map((item, index) => (
                  <div
                    key={`cash-${index}`}
                    className="p-1 px-2 mb-1 rounded bg-light d-flex justify-content-between align-items-center border-start border-3 border-primary"
                    style={{ fontSize: '0.72rem' }}
                  >
                    <span
                      className="fw-semibold text-truncate me-1"
                      style={{ maxWidth: '110px' }}
                    >
                      {item.name}
                    </span>
                    <span className="fw-bold text-dark">
                      ₹{parseFloat(item.balance || 0).toLocaleString('en-IN')}
                    </span>
                  </div>
                ))}

                {bankAccounts.map((item, index) => (
                  <div
                    key={`bank-${index}`}
                    className="p-1 px-2 mb-1 rounded bg-light d-flex justify-content-between align-items-center border-start border-3 border-success"
                    style={{ fontSize: '0.72rem' }}
                  >
                    <span
                      className="fw-semibold text-truncate me-1"
                      style={{ maxWidth: '110px' }}
                    >
                      {item.name}
                    </span>
                    <span className="fw-bold text-dark">
                      ₹{parseFloat(item.balance || 0).toLocaleString('en-IN')}
                    </span>
                  </div>
                ))}

                {cashAccounts.length === 0 && bankAccounts.length === 0 && (
                  <div
                    className="text-muted text-center py-2"
                    style={{ fontSize: '0.72rem' }}
                  >
                    No cash/bank balances.
                  </div>
                )}
              </div>
            </Card>
          </Col>
        </Row>

        {/* Compact Quick Shortcuts Strip */}
        <div className="dash-card-compact p-2 d-flex align-items-center gap-1 flex-wrap">
          <span
            className="text-muted fw-bold me-1 text-uppercase"
            style={{ fontSize: '0.68rem' }}
          >
            Quick Launch:
          </span>

          <Link to="/batch-fee-due" className="dash-quick-pill">
            <FaCalendarCheck className="text-warning" />
            <span>Batch Fee Due</span>
          </Link>

          <Link to="/receipt" className="dash-quick-pill">
            <FaFileInvoice className="text-success" />
            <span>Receipt</span>
          </Link>

          <Link to="/fee-summary" className="dash-quick-pill">
            <FaRupeeSign className="text-danger" />
            <span>Fee Summary</span>
          </Link>

          <Link to="/attendance-management" className="dash-quick-pill">
            <FaClipboardList className="text-primary" />
            <span>Attendance</span>
          </Link>

          <Link to="/create-student" className="dash-quick-pill">
            <FaUserPlus className="text-secondary" />
            <span>Admit Student</span>
          </Link>

          <Link to="/fees-structure" className="dash-quick-pill">
            <FaLayerGroup className="text-info" />
            <span>Fee Structures</span>
          </Link>
        </div>
      </Container>
    </Layout>
  );
};

export default Dashboard;
